// SAFE MODE: delivery sessions improvements
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { sendNotification } from '../../services/notificationService';
import Toast from '../../components/ui/Toast';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import mediaUploadService from '../../services/mediaUploadService';
import { createDeliveryLink } from '../../services/deliveryPageService';
import { Capacitor } from '@capacitor/core';
import { Media } from '@capacitor-community/media';
import { getPhotographyTypeLabelAr } from '../../utils/bookingLabels';

// ⭐ Interactive Star Rating Component
const StarRating = ({ rating, onRate, size = 28, interactive = true }) => {
  const [hoverRating, setHoverRating] = useState(0);
  
  return (
    <div className="flex items-center gap-1" dir="ltr">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={!interactive}
          onClick={() => interactive && onRate(star)}
          onMouseEnter={() => interactive && setHoverRating(star)}
          onMouseLeave={() => interactive && setHoverRating(0)}
          className={`transition-all duration-150 ${interactive ? 'cursor-pointer hover:scale-125' : 'cursor-default'}`}
        >
          <Icon
            name="Star"
            size={size}
            className={`transition-colors duration-150 ${
              star <= (hoverRating || rating)
                ? 'text-amber-400 fill-amber-400'
                : 'text-slate-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

const MyDeliveries = () => {
  const { user } = useAuth();
  const location = useLocation();
  const autoBookingId = location.state?.bookingId || null;
  const [deliveries, setDeliveries] = useState([]);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  // ─── حالة الحفظ النيتف — تُستعاد من localStorage عند العودة للصفحة ───────────
  const ALBUM_SAVE_KEY = 'mashhad_album_save_state';
  const _readSaveState = () => {
    try { return JSON.parse(localStorage.getItem(ALBUM_SAVE_KEY) || 'null'); } catch { return null; }
  };
  const [nativeSaving, setNativeSaving] = useState(() => _readSaveState()?.phase === 'saving');
  const [nativeSaveProgress, setNativeSaveProgress] = useState(() => _readSaveState());
  // nativeSaveProgress: { current, total, failed, firstError, phase: 'saving'|'done' } | null

  const saveProgressListenerRef = useRef(null);
  const saveCompleteListenerRef = useRef(null);

  const _persistSaveState = (state) => {
    try {
      if (state) localStorage.setItem(ALBUM_SAVE_KEY, JSON.stringify(state));
      else localStorage.removeItem(ALBUM_SAVE_KEY);
    } catch {}
  };

  // دالة مشتركة لربط listeners — تُستدعى عند بدء الحفظ وعند إعادة تحميل الصفحة
  const attachSaveListeners = useCallback(async () => {
    // تجنّب تكرار الربط
    saveProgressListenerRef.current?.remove();
    saveCompleteListenerRef.current?.remove();

    saveProgressListenerRef.current = await Media.addListener('albumSaveProgress', (data) => {
      const next = { current: data.current, total: data.total ?? data.current, failed: data.failed, firstError: null, phase: 'saving' };
      setNativeSaveProgress(prev => prev ? { ...prev, current: data.current, failed: data.failed } : next);
      _persistSaveState({ ..._readSaveState(), current: data.current, failed: data.failed, phase: 'saving' });
    });

    saveCompleteListenerRef.current = await Media.addListener('albumSaveComplete', (data) => {
      saveProgressListenerRef.current?.remove();
      saveCompleteListenerRef.current?.remove();
      saveProgressListenerRef.current = null;
      saveCompleteListenerRef.current = null;

      const doneState = {
        current: data.saved, total: data.total,
        failed: data.failed, firstError: data.failed > 0 ? `${data.failed} ملف لم يُحفظ` : null,
        phase: 'done',
      };
      setNativeSaving(false);
      setNativeSaveProgress(doneState);
      _persistSaveState(doneState);

      if (data.failed === 0) {
        setToast({ type: 'success', message: `✅ تم حفظ ${data.total} ملف في استوديو الصور — ألبوم "مشهد"` });
      } else if (data.saved > 0) {
        setToast({ type: 'info', message: `تم حفظ ${data.saved} من ${data.total} ملف (${data.failed} لم يُحفظ)` });
      } else {
        setToast({ type: 'error', message: 'فشل حفظ الملفات. تحقق من الاتصال والصلاحيات.' });
      }
      setTimeout(() => { setNativeSaveProgress(null); _persistSaveState(null); }, 6000);
    });
  }, []);

  // عند تحميل الصفحة — إعادة ربط listeners إذا كان الحفظ جارياً
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const saved = _readSaveState();
    if (saved?.phase === 'saving') {
      attachSaveListeners();
    }
    return () => {
      // عند مغادرة الصفحة: إزالة الـ listeners فقط — localStorage يبقى
      saveProgressListenerRef.current?.remove();
      saveCompleteListenerRef.current?.remove();
      saveProgressListenerRef.current = null;
      saveCompleteListenerRef.current = null;
    };
  }, [attachSaveListeners]);
  const [toast, setToast] = useState(null);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [urlMap, setUrlMap] = useState({});
  const [visibleCount, setVisibleCount] = useState(null);
  const [isDesktopWeb, setIsDesktopWeb] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(pointer: fine) and (hover: hover)');
    const update = () => {
      setIsDesktopWeb(!Capacitor.isNativePlatform() && mq.matches);
    };
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // Review states
  const [existingReview, setExistingReview] = useState(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  // booking_id من إشعار Push التذكير (محفوظ في localStorage)
  const [pushReviewBookingId, setPushReviewBookingId] = useState(() => {
    try {
      const id = localStorage.getItem('review_reminder_booking_id');
      if (id) { localStorage.removeItem('review_reminder_booking_id'); return id; }
    } catch {}
    return null;
  });
  // SAFE MODE: delivery URL modal fallback if Browser.open fails
  const [deliveryUrlModal, setDeliveryUrlModal] = useState(null);

  // Dispute state
  const [showDisputeForm, setShowDisputeForm] = useState(false);
  const [disputeType, setDisputeType] = useState('');
  const [disputeMessage, setDisputeMessage] = useState('');
  const [submittingDispute, setSubmittingDispute] = useState(false);
  const [disputeResult, setDisputeResult] = useState(null); // null | { ok, ticket_number, message, already_submitted }

  const DISPUTE_LABELS = {
    missing_photos: 'صور ناقصة أو أقل من المتفق عليه',
    low_quality: 'جودة الصور سيئة أو غير مقبولة',
    wrong_editing: 'التعديل لم يتم على الصور',
    other: 'مشكلة أخرى',
  };

  const handleSubmitDispute = async () => {
    if (!disputeType || !selectedDelivery) return;
    const finalMessage = disputeMessage.trim() || DISPUTE_LABELS[disputeType] || disputeType;
    setSubmittingDispute(true);
    try {
      const { data: result, error } = await supabase.functions.invoke('submit-delivery-dispute', {
        body: {
          delivery_id: selectedDelivery.id,
          dispute_type: disputeType,
          message: finalMessage,
        },
      });
      if (error) {
        setDisputeResult({ ok: false, message: error.message || 'حدث خطأ' });
      } else {
        setDisputeResult(result);
        if (result?.ok || result?.already_submitted) setShowDisputeForm(false);
      }
    } catch (e) {
      setDisputeResult({ ok: false, message: 'تعذّر الاتصال بالخادم' });
    } finally {
      setSubmittingDispute(false);
    }
  };

  // Album Viewer modal state
  const [showAlbumViewer, setShowAlbumViewer] = useState(false);
  const [albumLightboxIndex, setAlbumLightboxIndex] = useState(null);
  const [albumFullUrls, setAlbumFullUrls] = useState(null); // array of signedUrl per file index
  const fetchingAlbumFullUrls = useRef(false);

  const fetchAlbumFullUrls = useCallback(async (fileList) => {
    if (fetchingAlbumFullUrls.current || !fileList?.length) return;
    fetchingAlbumFullUrls.current = true;
    try {
      const paths = fileList.map(f => f.file_path);
      const result = await mediaUploadService.getSignedUrls(paths);
      setAlbumFullUrls((result || []).map(item => item?.signedUrl ?? null));
    } catch {
      setAlbumFullUrls([]);
    }
  }, []);

  const handleOpenAlbumViewer = () => {
    setShowAlbumViewer(true);
    setAlbumLightboxIndex(null);
    setAlbumFullUrls(null);
    fetchingAlbumFullUrls.current = false;
  };

  // SAFE MODE: viewed_sessions for persistent "New" badge fix (PART 4)
  const VIEWED_SESSIONS_KEY = 'viewed_sessions';
  const [viewedSessions, setViewedSessions] = useState(() => {
    try {
      const raw = localStorage.getItem(VIEWED_SESSIONS_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  const markSessionViewed = (deliveryId) => {
    try {
      const prev = { ...viewedSessions, [deliveryId]: true };
      localStorage.setItem(VIEWED_SESSIONS_KEY, JSON.stringify(prev));
      setViewedSessions(prev);
    } catch {}
  };

  useEffect(() => {
    if (!user) return;
    loadDeliveries();
  }, [user]);

  // Auto-select delivery when navigated from a specific booking (location.state أو Push notification)
  useEffect(() => {
    const targetBookingId = autoBookingId || pushReviewBookingId;
    if (!targetBookingId || deliveries.length === 0 || selectedDelivery) return;
    const match = deliveries.find((d) => d.booking_id === targetBookingId);
    if (match) {
      handleSelectDelivery(match);
      // إذا كان من إشعار التذكير ولا يوجد تقييم سابق — افتح المودال تلقائياً
      if (pushReviewBookingId && !match.hasReview) {
        setShowReviewModal(true);
      }
    }
  }, [deliveries, autoBookingId, pushReviewBookingId]);

  // SAFE MODE: Hydrate viewedSessions from localStorage on mount (PART 4)
  useEffect(() => {
    try {
      const raw = localStorage.getItem('viewed_sessions');
      if (raw) setViewedSessions(JSON.parse(raw));
    } catch {}
  }, []);

  const loadDeliveries = async () => {
    setLoading(true);
    try {
      // First get deliveries
      const { data: deliveriesData, error: deliveriesError } = await supabase
        .from('booking_deliveries')
        .select(`
          *,
          booking:booking_id (
            id,
            booking_date,
            booking_time,
            photography_type,
            city
          )
        `)
        .eq('customer_id', user.id)
        .in('status', ['delivered', 'downloaded'])
        .order('delivered_at', { ascending: false });

      if (deliveriesError) throw deliveriesError;

      // Get photographer info and existing reviews
      if (deliveriesData && deliveriesData.length > 0) {
        const photographerIds = [...new Set(deliveriesData.map(d => d.photographer_id).filter(Boolean))];
        const bookingIds = deliveriesData.map(d => d.booking_id).filter(Boolean);
        
        const [photographersResult, reviewsResult] = await Promise.all([
          supabase.from('user_profiles').select('id, full_name, avatar_url').in('id', photographerIds),
          supabase.from('reviews').select('booking_id, rating').eq('customer_id', user.id).in('booking_id', bookingIds)
        ]);

        const photographersMap = {};
        photographersResult.data?.forEach(p => { photographersMap[p.id] = p; });

        const reviewsMap = {};
        reviewsResult.data?.forEach(r => { reviewsMap[r.booking_id] = r; });

        const enrichedDeliveries = deliveriesData.map(d => ({
          ...d,
          photographer: photographersMap[d.photographer_id] || null,
          hasReview: !!reviewsMap[d.booking_id],
          reviewRating: reviewsMap[d.booking_id]?.rating || 0
        }));

        setDeliveries(enrichedDeliveries);
      } else {
        setDeliveries([]);
      }
      
    } catch (error) {
      console.error('Error loading deliveries:', error);
      setToast({ type: 'error', message: 'فشل تحميل التسليمات' });
    } finally {
      setLoading(false);
    }
  };

  const loadFiles = async (deliveryId) => {
    try {
      const { data, error } = await supabase
        .from('delivery_files')
        .select('*')
        .eq('delivery_id', deliveryId)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      const list = data || [];
      setFiles(list);
      setUrlMap({});
      setVisibleCount(list.length > 100 ? 30 : list.length);
    } catch (error) {
      console.error('Error loading files:', error);
      setToast({ type: 'error', message: 'فشل تحميل الملفات' });
    }
  };

  // SAFE MODE: Batch signed URLs for visible files - prefer thumbnail_path for lightweight preview (PART 6B)
  useEffect(() => {
    if (!files.length || visibleCount === null) return;
    const visible = files.slice(0, visibleCount);
    const paths = [];
    visible.forEach((f) => {
      if (f.thumbnail_path) paths.push(f.thumbnail_path);
      else if (f.file_path) paths.push(f.file_path);
    });
    const uniquePaths = [...new Set(paths)];
    if (uniquePaths.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        const result = await mediaUploadService.getSignedUrls(uniquePaths);
        if (cancelled) return;
        const next = {};
        (result || []).forEach((item) => {
          if (item?.path && item?.signedUrl) next[item.path] = item.signedUrl;
        });
        setUrlMap((prev) => ({ ...prev, ...next }));
      } catch (e) {
        if (!cancelled) setToast({ type: 'error', message: 'فشل تحميل روابط الملفات' });
      }
    })();
    return () => { cancelled = true; };
  }, [files, visibleCount]);

  // Load existing review for a booking
  const loadReview = async (bookingId) => {
    if (!user || !bookingId) return;
    setReviewLoading(true);
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('booking_id', bookingId)
        .eq('customer_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows
      
      if (data) {
        setExistingReview(data);
        setReviewRating(data.rating);
        setReviewComment(data.comment || '');
      } else {
        setExistingReview(null);
        setReviewRating(0);
        setReviewComment('');
      }
    } catch (error) {
      console.error('Error loading review:', error);
    } finally {
      setReviewLoading(false);
    }
  };

  // Submit review (one-time only, not editable after submission)
  const handleSubmitReview = async () => {
    if (!selectedDelivery || !user || reviewRating === 0) {
      setToast({ type: 'error', message: 'يرجى اختيار تقييم بالنجوم أولاً' });
      return;
    }
    if (existingReview) return;

    setSubmittingReview(true);
    try {
      const bookingId = selectedDelivery.booking_id;
      const photographerId = selectedDelivery.photographer_id;
      const photographerName = selectedDelivery.photographer?.full_name || 'المصور';

      // Create new review
      const { error } = await supabase
        .from('reviews')
        .insert({
          booking_id: bookingId,
          customer_id: user.id,
          photographer_id: photographerId,
          rating: reviewRating,
          comment: reviewComment.trim() || null
        });

      if (error) throw error;

      // Get customer name
      const { data: customerData } = await supabase
        .from('user_profiles')
        .select('full_name')
        .eq('id', user.id)
        .single();
      const customerName = customerData?.full_name || 'عميل';

      // 🔔 Send notification to admin for moderation
      try {
        await supabase.from('admin_notifications').insert({
          type: 'new_review',
          title: 'تقييم جديد',
          message: `قام العميل ${customerName} بتقييم المصور ${photographerName} بـ ${reviewRating} نجوم.`,
          related_user_id: user.id,
          related_data: {
            booking_id: bookingId,
            photographer_id: photographerId,
            photographer_name: photographerName,
            customer_name: customerName,
            rating: reviewRating,
            comment: reviewComment.trim() || null
          }
        });
      } catch (e) { console.error('Failed to notify admin:', e); }

      // 🔔 Send notification to photographer (in-app + FCM push)
      try {
        await sendNotification({
          userId: photographerId,
          type: 'new_review',
          title: '⭐ تقييم جديد',
          message: `حصلت على تقييم ${reviewRating} نجوم من ${customerName}${reviewComment.trim() ? `: "${reviewComment.trim().substring(0, 100)}"` : ''}`,
          bookingId,
          fcmData: { type: 'NEW_REVIEW', rating: String(reviewRating) },
        });
      } catch (e) { console.error('Failed to notify photographer:', e); }

      setToast({ type: 'success', message: '✅ شكراً لك! تم إرسال تقييمك بنجاح' });

      // Reload review
      await loadReview(bookingId);
    } catch (error) {
      console.error('Error submitting review:', error);
      setToast({ type: 'error', message: 'فشل إرسال التقييم: ' + (error.message || 'حدث خطأ') });
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleSelectDelivery = async (delivery) => {
    setSelectedDelivery(delivery);
    setExistingReview(null);
    setReviewRating(0);
    setReviewComment('');
    setShowDisputeForm(false);
    setDisputeType('');
    setDisputeMessage('');
    setDisputeResult(null);
    await loadFiles(delivery.id);
    await loadReview(delivery.booking_id);

    // SAFE MODE: Mark session as viewed in localStorage (PART 4) - removes "New" badge permanently
    markSessionViewed(delivery.id);

    // Mark as first download if not already
    if (!delivery.first_download_at) {
      const now = new Date().toISOString();
      await supabase
        .from('booking_deliveries')
        .update({
          status: 'downloaded',
          first_download_at: now,
        })
        .eq('id', delivery.id);

      setDeliveries((prev) =>
        prev.map((d) =>
          d.id === delivery.id
            ? { ...d, status: 'downloaded', first_download_at: now }
            : d
        )
      );
    }
  };

  const getFileUrl = async (filePath) => {
    return await mediaUploadService.getSignedUrl(filePath);
  };

  const handleConfirmDelete = async () => {
    if (!fileToDelete || deleting) return;
    setDeleting(true);
    try {
      const { error: storageError } = await supabase.storage
        .from('deliveries')
        .remove([fileToDelete.file_path]);

      if (storageError) throw storageError;

      const { error: dbError } = await supabase
        .from('delivery_files')
        .delete()
        .eq('id', fileToDelete.id);

      if (dbError) throw dbError;

      setFiles((prev) => prev.filter((f) => f.id !== fileToDelete.id));
      setUrlMap((prev) => {
        const next = { ...prev };
        delete next[fileToDelete.file_path];
        if (fileToDelete.thumbnail_path) delete next[fileToDelete.thumbnail_path];
        return next;
      });
      if (selectedDelivery) {
        const isPhoto = fileToDelete.file_type === 'image';
        setSelectedDelivery((prev) => ({
          ...prev,
          total_photos: Math.max(0, (prev.total_photos || 0) - (isPhoto ? 1 : 0)),
          total_videos: Math.max(0, (prev.total_videos || 0) - (isPhoto ? 0 : 1)),
          total_size_bytes: Math.max(0, (prev.total_size_bytes || 0) - (fileToDelete.file_size_bytes || 0))
        }));
      }
      setToast({ type: 'success', message: 'تم حذف الملف' });
    } catch (error) {
      console.error('Error deleting file:', error);
      setToast({ type: 'error', message: 'فشل حذف الملف: ' + (error.message || '') });
    } finally {
      setDeleting(false);
      setFileToDelete(null);
    }
  };

  // SAFE MODE: Open delivery page in external browser (PART 0, PART 1)
  const handleDownloadFullAlbum = async () => {
    if (!selectedDelivery || !user) {
      setToast({ type: 'error', message: 'يرجى اختيار جلسة أولاً' });
      return;
    }
    setDownloading(true);
    setToast({ type: 'info', message: 'جاري تحضير رابط التحميل...' });
    try {
      // Use supabase.functions.invoke — handles token refresh automatically,
      // avoids "Invalid JWT" errors from manual fetch with a stale token.
      const { data: result, error: fnError } = await supabase.functions.invoke(
        'create-delivery-link',
        { body: { delivery_id: selectedDelivery.id } }
      );

      if (fnError) {
        // Extract real error message from the response body
        let errMsg = fnError.message || 'فشل الاتصال بالخادم';
        try {
          const body = await fnError.context?.json?.();
          if (body?.message) errMsg = body.message;
        } catch {}
        console.error('create-delivery-link error:', errMsg, fnError);
        setToast({ type: 'error', message: errMsg });
        return;
      }

      if (!result?.ok || !result?.url) {
        const msg = result?.message || 'فشل إنشاء رابط التحميل';
        setToast({ type: 'error', message: msg });
        return;
      }

      // Show URL modal — the <a href target="_blank"> inside triggers Capacitor's
      // native URL handler (UIApplication.shared.open → Safari) more reliably
      // than window.open which may silently fail in some WebView configurations.
      setDeliveryUrlModal(result.url);

    } catch (error) {
      console.error('handleDownloadFullAlbum:', error);
      setToast({ type: 'error', message: error?.message || 'حدث خطأ غير متوقع' });
    } finally {
      setDownloading(false);
    }
  };

  // ─── حفظ في استوديو الصور — Background URLSession (لا يمس JS heap) ──────────
  const handleNativeSaveToStudio = async () => {
    if (!selectedDelivery || !files.length) {
      setToast({ type: 'error', message: 'يرجى اختيار جلسة أولاً' });
      return;
    }
    if (!Capacitor.isNativePlatform()) {
      setToast({ type: 'error', message: 'هذه الميزة متاحة فقط في تطبيق الجوال' });
      return;
    }

    // 1. صلاحية مكتبة الصور
    try {
      const permStatus = await Media.checkPermissions();
      if (permStatus.photos !== 'granted') {
        const req = await Media.requestPermissions();
        if (req.photos !== 'granted') {
          setToast({ type: 'error', message: 'يحتاج التطبيق إذن الوصول إلى استوديو الصور — فعّله من الإعدادات' });
          return;
        }
      }
    } catch (e) {
      const msg = e?.message || '';
      if (!msg.includes('not implemented') && !msg.includes('unimplemented')) {
        setToast({ type: 'error', message: 'فشل التحقق من الصلاحيات: ' + msg });
        return;
      }
    }

    // 2. جلب Signed URLs
    let signedUrls = [];
    try {
      setToast({ type: 'info', message: 'جاري تحضير الملفات...' });
      const paths = files.map(f => f.file_path);
      const signedData = await mediaUploadService.getSignedUrls(paths);
      signedUrls = (signedData || []).map(item => item?.signedUrl).filter(Boolean);
    } catch (e) {
      setToast({ type: 'error', message: 'فشل جلب روابط الملفات' });
      return;
    }
    if (!signedUrls.length) {
      setToast({ type: 'error', message: 'لا توجد ملفات للحفظ' });
      return;
    }

    // 3. تهيئة واجهة التقدم + حفظ الحالة في localStorage
    const total = signedUrls.length;
    const initialState = { current: 0, total, failed: 0, firstError: null, phase: 'saving' };
    setNativeSaving(true);
    setNativeSaveProgress(initialState);
    _persistSaveState(initialState);

    // 4. ربط listeners المشتركة
    await attachSaveListeners();

    // 5. استدعاء saveAlbum — Swift يتولى كل شيء
    try {
      await Media.saveAlbum({ urls: signedUrls, albumName: 'مشهد' });
    } catch (e) {
      saveProgressListenerRef.current?.remove();
      saveCompleteListenerRef.current?.remove();
      saveProgressListenerRef.current = null;
      saveCompleteListenerRef.current = null;
      const errState = { current: 0, total, failed: total, firstError: e?.message || 'فشل بدء الحفظ', phase: 'done' };
      setNativeSaving(false);
      setNativeSaveProgress(errState);
      _persistSaveState(errState);
      setToast({ type: 'error', message: 'فشل بدء الحفظ: ' + (e?.message || 'unknown') });
      setTimeout(() => { setNativeSaveProgress(null); _persistSaveState(null); }, 6000);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6" dir="rtl">
      <h1 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
        <Icon name="Download" size={24} className="text-primary" />
        صوري وفيديوهاتي
      </h1>

      {deliveries.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
          <Icon name="ImageOff" size={64} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">لا توجد تسليمات بعد</h3>
          <p className="text-slate-500">ستظهر صورك وفيديوهاتك هنا بعد أن يقوم المصور بتسليمها</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Deliveries List */}
          <div className="lg:col-span-1 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 bg-gradient-to-l from-primary/5 to-white">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-bold text-slate-800">الجلسات المسلّمة</h2>
                <span className="text-xs font-medium bg-primary/10 text-primary px-2.5 py-1 rounded-full">{deliveries.length}</span>
              </div>
              {/* تلميح للجوال فقط */}
              {!selectedDelivery && (
                <p className="text-xs text-primary/70 mt-2 flex items-center gap-1 lg:hidden">
                  <Icon name="TouchpadIcon" size={12} />
                  اضغط على أي جلسة لعرض صورها وفيديوهاتها
                </p>
              )}
            </div>

            <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
              {deliveries.map((delivery) => (
                <button
                  key={delivery.id}
                  onClick={() => handleSelectDelivery(delivery)}
                  className={`w-full p-4 text-right transition-all hover:bg-slate-50 active:bg-primary/5 ${
                    selectedDelivery?.id === delivery.id
                      ? 'bg-primary/10 border-r-4 border-primary shadow-sm'
                      : 'bg-white'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name="Camera" size={20} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-bold text-slate-900 truncate`}>
                        {getPhotographyTypeLabelAr(delivery.booking?.photography_type) || 'جلسة تصوير'}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {formatDate(delivery.booking?.booking_date)}
                      </p>
                      <div className="flex items-center gap-2.5 mt-1.5 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <Icon name="Image" size={11} />
                          {delivery.total_photos}
                        </span>
                        <span className="text-slate-200">|</span>
                        <span className="flex items-center gap-1">
                          <Icon name="Video" size={11} />
                          {delivery.total_videos}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col items-center gap-1.5 shrink-0">
                    {delivery.status === 'delivered' && !delivery.first_download_at && !viewedSessions[delivery.id] ? (
                        <span className="bg-button text-button-foreground text-xs px-2 py-0.5 rounded-full animate-pulse">
                        جديد
                      </span>
                    ) : delivery.hasReview ? (
                      <div className="flex items-center gap-0.5">
                          <Icon name="Star" size={11} className="text-amber-400 fill-amber-400" />
                        <span className="text-xs font-medium text-amber-600">{delivery.reviewRating}</span>
                      </div>
                      ) : null}
                      <Icon
                        name="ChevronLeft"
                        size={16}
                        className={selectedDelivery?.id === delivery.id ? 'text-primary' : 'text-slate-300'}
                      />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Files View */}
          <div className="lg:col-span-2">
            {!selectedDelivery ? (
              <div className="hidden lg:flex bg-white rounded-2xl shadow-sm border border-slate-200 p-12 flex-col items-center justify-center text-center">
                <div className="w-16 h-16 rounded-2xl bg-primary/5 flex items-center justify-center mx-auto mb-4">
                  <Icon name="GalleryVertical" size={32} className="text-primary/40" />
                </div>
                <p className="text-slate-500 font-medium">اختر جلسة من القائمة</p>
                <p className="text-slate-400 text-sm mt-1">ستظهر صورك وفيديوهاتك هنا</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* SAFE MODE: Session header + delivery info (PART 6, PART 7) */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                  {/* رأس البطاقة: نوع الجلسة + تاريخها */}
                  <div className="mb-4">
                    <h3 className="text-base font-bold text-slate-900">
                      {getPhotographyTypeLabelAr(selectedDelivery.booking?.photography_type) || 'جلسة تصوير'}
                    </h3>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {selectedDelivery.total_photos} صورة • {selectedDelivery.total_videos} فيديو
                    </p>
                  </div>
                  {/* معلومات المصور */}
                  <div className="flex items-center gap-3 pt-3 border-t border-slate-100">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-100 flex-shrink-0">
                      {selectedDelivery.photographer?.avatar_url ? (
                        <Image 
                          src={selectedDelivery.photographer.avatar_url} 
                          alt="" 
                          className="w-full h-full object-cover" 
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Icon name="User" size={18} className="text-slate-400" />
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">
                        {selectedDelivery.photographer?.full_name || 'المصور'}
                      </p>
                      <p className="text-xs text-slate-400">
                        سُلِّم {formatDate(selectedDelivery.delivered_at)}
                      </p>
                    </div>
                  </div>

                  {/* أزرار الجلسة */}
                  <div className="mt-4 pt-4 border-t border-slate-100">

                    {/* زر تحميل الألبوم — يظهر على الويب فقط */}
                    {!Capacitor.isNativePlatform() && (
                    <Button
                      onClick={handleDownloadFullAlbum}
                        disabled={downloading || nativeSaving}
                      className="bg-button hover:opacity-90 text-button-foreground w-full mt-4"
                      iconName="DownloadCloud"
                      iconPosition="right"
                    >
                      {downloading ? 'جاري التحضير...' : 'تحميل الألبوم كاملاً'}
                    </Button>
                    )}

                    {/* زر عرض وتحميل — ويب الجوال + تطبيق الجوال فقط */}
                    {!isDesktopWeb && (
                    <Button
                      onClick={handleOpenAlbumViewer}
                      disabled={downloading || !files.length}
                      className="w-full mt-3 bg-slate-900 hover:bg-slate-800 text-white"
                      iconName="LayoutGrid"
                      iconPosition="right"
                    >
                      عرض وتحميل
                    </Button>
                    )}

                    {/* شريط تقدم الحفظ — يظهر في البطاقة فقط عند إغلاق النافذة الداكنة أثناء الحفظ */}
                    {Capacitor.isNativePlatform() && !showAlbumViewer && (nativeSaving || nativeSaveProgress?.phase === 'done') && (
                      <div className="mt-3">
                        {nativeSaving && nativeSaveProgress && nativeSaveProgress.total > 0 && (
                          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                            <div className="flex items-center justify-between text-xs text-emerald-700 mb-2 font-medium">
                              <span className="flex items-center gap-1.5">
                                <span className="w-3 h-3 border-2 border-emerald-400 border-t-emerald-700 rounded-full animate-spin" />
                                جاري الحفظ في استوديو الصور...
                              </span>
                              <span className="font-mono">
                                {nativeSaveProgress.current}/{nativeSaveProgress.total}
                              </span>
                  </div>
                            <div className="w-full bg-emerald-200 rounded-full h-1.5">
                              <div
                                className="bg-emerald-600 h-1.5 rounded-full transition-all duration-300"
                                style={{ width: `${(nativeSaveProgress.current / nativeSaveProgress.total) * 100}%` }}
                              />
                </div>
                            <p className="text-xs text-amber-600 mt-2 text-center font-medium">
                              لا تغادر هذه الصفحة حتى اكتمال الحفظ
                            </p>
                          </div>
                        )}

                        {!nativeSaving && nativeSaveProgress?.phase === 'done' && (
                          <div className={`rounded-xl px-4 py-3 text-xs text-center font-medium border ${
                            nativeSaveProgress.failed === 0
                              ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                              : nativeSaveProgress.failed < nativeSaveProgress.total
                              ? 'bg-amber-50 border-amber-200 text-amber-700'
                              : 'bg-red-50 border-red-200 text-red-700'
                          }`}>
                            {nativeSaveProgress.failed === 0
                              ? `✅ تم الحفظ في ألبوم "مشهد" في استوديو صورك`
                              : nativeSaveProgress.failed < nativeSaveProgress.total
                              ? `⚠️ تم حفظ ${nativeSaveProgress.total - nativeSaveProgress.failed} من ${nativeSaveProgress.total} ملف`
                              : `❌ فشل حفظ جميع الملفات`}
                          </div>
                        )}
                      </div>
                    )}

                    {/* ── زرا التقييم والإبلاغ ── */}
                    <div className="flex gap-2 mt-3">
                      {/* تقييم */}
                      {!existingReview ? (
                        <button
                          onClick={() => setShowReviewModal(true)}
                          className="flex-1 flex items-center justify-center gap-1.5 border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-700 font-semibold py-2.5 rounded-xl transition text-sm"
                        >
                          <Icon name="Star" size={15} className="fill-amber-400 text-amber-400" />
                          قيّم المصور
                        </button>
                      ) : (
                        <div className="flex-1 flex items-center justify-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-600 py-2.5 rounded-xl text-sm">
                          <Icon name="Star" size={14} className="fill-amber-400 text-amber-400" />
                          <span className="font-semibold">{selectedDelivery.reviewRating}</span>
                          <span className="text-amber-500">/ 5</span>
                        </div>
                      )}

                      {/* إبلاغ */}
                      <button
                        onClick={() => { setShowDisputeForm(v => !v); setDisputeResult(null); }}
                        className="flex-1 flex items-center justify-center gap-1.5 border border-slate-200 hover:bg-red-50 hover:border-red-200 hover:text-red-600 text-slate-500 font-medium py-2.5 rounded-xl transition text-sm"
                      >
                        <Icon name="AlertTriangle" size={15} />
                        ابلاغ عن مشكلة
                      </button>
                    </div>

                    {/* ── نموذج الإبلاغ (inline) ── */}
                    {showDisputeForm && !disputeResult?.ok && !disputeResult?.already_submitted && (
                      <div className="mt-3 bg-red-50 border border-red-200 rounded-2xl p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-red-800 flex items-center gap-1.5">
                            <Icon name="AlertTriangle" size={14} />
                            إبلاغ عن مشكلة في التسليم
                          </p>
                          <button onClick={() => setShowDisputeForm(false)} className="text-red-400 hover:text-red-600">
                            <Icon name="X" size={15} />
                          </button>
                        </div>

                        <div className="grid grid-cols-1 gap-1.5">
                          {[
                            { value: 'missing_photos', label: 'صور ناقصة أو أقل من المتفق عليه' },
                            { value: 'low_quality',    label: 'جودة الصور سيئة أو غير مقبولة' },
                            { value: 'wrong_editing',  label: 'التعديل لم يتم على الصور' },
                            { value: 'other',          label: 'مشكلة أخرى' },
                          ].map(opt => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setDisputeType(opt.value)}
                              className={`text-right text-sm px-3 py-2 rounded-xl border transition-colors ${
                                disputeType === opt.value
                                  ? 'bg-red-600 text-white border-red-600'
                                  : 'bg-white text-slate-700 border-slate-200 hover:border-red-300'
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>

                        <textarea
                          value={disputeMessage}
                          onChange={e => setDisputeMessage(e.target.value)}
                          placeholder="تفاصيل إضافية (اختياري)..."
                          rows={3}
                          className="w-full border border-red-200 bg-white rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-300"
                        />

                        {disputeResult?.ok === false && (
                          <p className="text-xs text-red-700 bg-red-100 border border-red-200 rounded-xl px-3 py-2">
                            {disputeResult.message}
                          </p>
                        )}

                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setShowDisputeForm(false)}
                            className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 bg-white transition"
                          >
                            إلغاء
                          </button>
                          <button
                            type="button"
                            onClick={handleSubmitDispute}
                            disabled={submittingDispute || !disputeType}
                            className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white font-bold text-sm transition flex items-center justify-center gap-1.5"
                          >
                            {submittingDispute
                              ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              : <><Icon name="Send" size={13} />إرسال الشكوى</>}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* رسالة نجاح الإرسال */}
                    {(disputeResult?.ok || disputeResult?.already_submitted) && (
                      <div className="mt-3 flex items-start gap-2 bg-green-50 border border-green-200 rounded-2xl px-4 py-3 text-sm text-green-800">
                        <Icon name="CheckCircle2" size={16} className="text-green-500 shrink-0 mt-0.5" />
                        <span>
                          {disputeResult.already_submitted
                            ? `أبلغت عن مشكلة في هذا التسليم مسبقاً${disputeResult.ticket_number ? ` — رقم التذكرة: ${disputeResult.ticket_number}` : ''}`
                            : `تم إرسال شكواك بنجاح${disputeResult.ticket_number ? ` — رقم التذكرة: ${disputeResult.ticket_number}` : ''}. سيتواصل معك فريق الدعم قريباً.`}
                        </span>
                      </div>
                    )}

                  </div>
                </div>

              </div>
            )}
          </div>
        </div>
      )}

      {fileToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" dir="rtl">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <p className="text-slate-800 mb-6">
              عند تاكيد الحذف سيتم حذف الصور والفيديوهات نهائيا من المعرض وإزالتها بشكل كامل من النظام، ولن تستطيع إستعادتها لاحقا.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                onClick={() => setFileToDelete(null)}
                disabled={deleting}
                className="bg-slate-200 text-slate-700 hover:bg-slate-300"
              >
                إلغاء
              </Button>
              <Button
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
                iconName={deleting ? 'Loader2' : 'Trash2'}
              >
                {deleting ? 'جاري الحذف...' : 'تأكيد الحذف'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Review Modal ── */}
      {showReviewModal && selectedDelivery && !existingReview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" dir="rtl">
          <div className="bg-white rounded-3xl shadow-2xl max-w-sm w-full p-7" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-5">
              <div className="w-14 h-14 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-3">
                <Icon name="Star" size={28} className="text-amber-400 fill-amber-400" />
              </div>
              <h3 className="font-bold text-slate-900 text-lg">قيّم تجربتك</h3>
              <p className="text-slate-500 text-sm mt-1">
                كيف كانت تجربتك مع {selectedDelivery.photographer?.full_name || 'المصور'}؟
              </p>
            </div>

            <div className="mb-2 flex justify-center">
              <StarRating rating={reviewRating} onRate={setReviewRating} size={36} interactive />
            </div>
            {reviewRating > 0 && (
              <p className="text-center text-sm mb-4 text-amber-500 font-medium">
                {['', 'ضعيف', 'مقبول', 'جيد', 'جيد جداً', 'ممتاز'][reviewRating]}
              </p>
            )}

            <textarea
              value={reviewComment}
              onChange={(e) => setReviewComment(e.target.value)}
              placeholder="أضف تعليقاً (اختياري)..."
              rows={3}
              className="w-full border-2 border-slate-200 focus:border-primary rounded-2xl px-4 py-3 text-sm resize-none focus:outline-none transition mb-4"
            />

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowReviewModal(false)}
                className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold py-3 rounded-2xl transition text-sm"
              >
                لاحقاً
              </button>
              <button
                type="button"
                onClick={async () => {
                  await handleSubmitReview();
                  setShowReviewModal(false);
                }}
                disabled={submittingReview || reviewRating === 0}
                className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-40 text-white font-bold py-3 rounded-2xl transition text-sm flex items-center justify-center gap-2"
              >
                {submittingReview
                  ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  : <><Icon name="Star" size={14} className="fill-white text-white" />إرسال التقييم</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delivery URL modal — uses <a href> which Capacitor routes to Safari reliably */}
      {deliveryUrlModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" dir="rtl">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <Icon name="CheckCircle2" size={24} className="text-green-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">الرابط جاهز!</h3>
                <p className="text-xs text-slate-500">صلاحيته 7 أيام</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 mb-5">
              اضغط الزر أدناه لفتح صفحة استلام صورك في المتصفح. ستحتاج إلى التحقق برقم جوالك.
            </p>
            <a
              href={deliveryUrlModal}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full text-center bg-primary text-white rounded-xl py-3.5 font-bold mb-3 active:opacity-80 transition-opacity"
              onClick={() => setTimeout(() => setDeliveryUrlModal(null), 500)}
            >
              <Icon name="ExternalLink" size={18} className="text-white" />
              فتح صفحة الاستلام في المتصفح
            </a>
            <button
              onClick={() => setDeliveryUrlModal(null)}
              className="block w-full text-center text-slate-400 text-sm py-2"
            >
              إغلاق
            </button>
          </div>
        </div>
      )}

      {/* Album Viewer Modal */}
      {showAlbumViewer && selectedDelivery && (
        <AlbumViewerModal
          files={files}
          urlMap={urlMap}
          delivery={selectedDelivery}
          lightboxIndex={albumLightboxIndex}
          fullUrls={albumFullUrls}
          onOpenLightbox={(i) => {
            setAlbumLightboxIndex(i);
            if (!fetchingAlbumFullUrls.current) fetchAlbumFullUrls(files);
          }}
          onCloseLightbox={() => setAlbumLightboxIndex(null)}
          onNavigate={(i) => setAlbumLightboxIndex(i)}
          onClose={() => { setShowAlbumViewer(false); setAlbumLightboxIndex(null); }}
          onDownload={handleDownloadFullAlbum}
          onNativeSave={handleNativeSaveToStudio}
          downloading={downloading}
          nativeSaving={nativeSaving}
          nativeSaveProgress={nativeSaveProgress}
          isNative={Capacitor.isNativePlatform()}
        />
      )}

      {toast && (
        <Toast
          notification={{
            title: toast.type === 'error' ? 'خطأ' : toast.type === 'success' ? 'تم' : 'تنبيه',
            message: toast.message,
            icon: toast.type === 'error' ? 'AlertCircle' : toast.type === 'success' ? 'CheckCircle2' : 'Info',
            iconColor: toast.type === 'error' ? '#ef4444' : toast.type === 'success' ? '#10b981' : '#3b82f6',
          }}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

// SAFE MODE: Customer File Card - VIEW ONLY, no single-item download (PART 0, 6, 6B)
const CustomerFileCard = ({ file, url: urlProp, thumbnailUrl: thumbnailUrlProp, getFileUrl, onDelete }) => {
  const [url, setUrl] = useState(urlProp ?? null);
  const [thumbnailUrl, setThumbnailUrl] = useState(thumbnailUrlProp ?? null);
  const [loading, setLoading] = useState(!urlProp && !thumbnailUrlProp);
  const [showPreview, setShowPreview] = useState(false);

  const resolvedUrl = urlProp ?? url;
  const resolvedThumbnail = thumbnailUrlProp ?? thumbnailUrl;

  useEffect(() => {
    const hasUrlFromParent = (typeof urlProp === 'string' && urlProp) || (typeof thumbnailUrlProp === 'string' && thumbnailUrlProp);
    if (hasUrlFromParent) {
      setUrl(urlProp ?? null);
      setThumbnailUrl(thumbnailUrlProp ?? null);
      setLoading(false);
      return;
    }
    const loadUrls = async () => {
      if (file.thumbnail_path) {
        const thumbUrl = await getFileUrl(file.thumbnail_path);
        setThumbnailUrl(thumbUrl);
      }
      const mainUrl = await getFileUrl(file.file_path);
      setUrl(mainUrl);
      setLoading(false);
    };
    loadUrls();
  }, [file.file_path, file.thumbnail_path, urlProp, thumbnailUrlProp]);

  // SAFE MODE: Use thumbnail/preview URL only - never full-res in gallery (PART 6B)
  const displayUrl = resolvedThumbnail || resolvedUrl;

  const preventSave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <>
      <div
        className="relative group rounded-xl overflow-hidden border border-slate-200 bg-slate-50 aspect-square shadow-sm hover:shadow-md transition-all hover:scale-[1.02] select-none"
        onContextMenu={preventSave}
        style={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none', userSelect: 'none' }}
      >
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        ) : file.file_type === 'video' ? (
          <div className="absolute inset-0">
            {displayUrl ? (
              <img src={displayUrl} alt={file.file_name} className="w-full h-full object-cover rounded-xl" loading="lazy" onContextMenu={preventSave} draggable={false} />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
                <Icon name="Film" size={32} className="text-slate-400" />
              </div>
            )}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center backdrop-blur-sm">
                <Icon name="Play" size={24} className="text-white ml-1" />
              </div>
            </div>
          </div>
        ) : (
          <img
            src={displayUrl}
            alt={file.file_name}
            className="w-full h-full object-cover rounded-xl cursor-pointer"
            loading="lazy"
            onClick={() => setShowPreview(true)}
            onContextMenu={preventSave}
            draggable={false}
          />
        )}

        {/* Overlay - SAFE MODE: no download button, view/preview only */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="absolute bottom-0 left-0 right-0 p-3 flex items-center justify-between">
            <button
              onClick={() => setShowPreview(true)}
              className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors backdrop-blur-sm"
            >
              <Icon name="Eye" size={16} className="text-white" />
            </button>
            {onDelete && (
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                className="p-2 bg-red-500/80 rounded-full hover:bg-red-500 transition-colors"
                title="حذف"
              >
                <Icon name="Trash2" size={16} className="text-white" />
              </button>
            )}
          </div>
        </div>

        {/* File type badge - SAFE MODE: no file size overlay (PART 6) */}
        <div className="absolute top-2 right-2">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
            file.file_type === 'video'
              ? 'bg-blue-500/80 text-white'
              : 'bg-white/80 text-slate-700'
          }`}>
            {file.file_type === 'video' ? 'فيديو' : 'صورة'}
          </span>
        </div>
      </div>

      {/* Preview Modal - SAFE MODE: view only, no download (PART 0) */}
      {showPreview && resolvedUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setShowPreview(false)}
          onContextMenu={(e) => e.preventDefault()}
        >
          <button
            className="absolute top-4 right-4 p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors z-10"
            onClick={() => setShowPreview(false)}
          >
            <Icon name="X" size={24} className="text-white" />
          </button>

          <div className="absolute bottom-4 left-4 bg-black/50 backdrop-blur-sm rounded-lg px-4 py-2 text-white text-sm z-10">
            <p className="font-medium truncate max-w-[200px]">{file.file_name}</p>
          </div>

          {file.file_type === 'video' ? (
            <video
              src={resolvedUrl}
              controls
              autoPlay
              className="max-w-full max-h-[90vh] rounded-lg"
              onClick={(e) => e.stopPropagation()}
              onContextMenu={(e) => e.preventDefault()}
            />
          ) : (
            <img
              src={resolvedUrl}
              alt={file.file_name}
              className="max-w-full max-h-[90vh] rounded-lg object-contain select-none"
              onClick={(e) => e.stopPropagation()}
              onContextMenu={(e) => e.preventDefault()}
              draggable={false}
              style={{ WebkitTouchCallout: 'none', pointerEvents: 'auto' }}
            />
          )}
        </div>
      )}
    </>
  );
};

// ─── AlbumViewerModal ─────────────────────────────────────────────────────────
// Full-screen dark gallery modal — same experience as DeliveryPage GalleryStep
// but embedded in the app (no OTP, uses already-loaded files + urlMap).

const AlbumLightbox = ({ files, currentIndex, thumbUrls, fullUrls, onClose, onPrev, onNext, onJump }) => {
  const file = files[currentIndex];
  const thumbUrl = thumbUrls[file?.id];
  const fullUrl = fullUrls?.[currentIndex] ?? null;
  const isVideo = file?.file_type === 'video';

  const [imgReady, setImgReady] = useState(false);
  const [isTouch, setIsTouch] = useState(false);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  useEffect(() => { setIsTouch(window.matchMedia('(pointer: coarse)').matches); }, []);
  useEffect(() => { setImgReady(false); }, [currentIndex]);

  // Lock body scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onNext();
      if (e.key === 'ArrowRight') onPrev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, onPrev, onNext]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = touchStartX.current - e.changedTouches[0].clientX;
    const dy = Math.abs((touchStartY.current ?? 0) - e.changedTouches[0].clientY);
    if (Math.abs(dx) > 50 && Math.abs(dx) > dy) { dx > 0 ? onNext() : onPrev(); }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < files.length - 1;

  return (
    <div
      className="fixed inset-0 z-[200] bg-black flex flex-col select-none"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onContextMenu={e => e.preventDefault()}
    >
      {/* Top bar */}
      <div className="shrink-0 flex items-center justify-between px-4 z-10"
        style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.75) 0%, transparent 100%)',
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 10px)',
          paddingBottom: '10px',
        }}>
        {/* spacer left */}
        <div className="w-8" />
        {/* counter center */}
        <div className="text-white text-sm font-mono bg-black/40 px-3 py-1 rounded-full" dir="ltr">
          {currentIndex + 1} / {files.length}
        </div>
        {/* close button right */}
        <button
          onClick={onClose}
          className="w-8 h-8 rounded-full flex items-center justify-center text-white transition active:scale-90"
          style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)' }}
        >
          <Icon name="X" size={16} />
        </button>
      </div>

      {/* Media area */}
      <div className="flex-1 flex items-center justify-center relative min-h-0 px-2">
        <div className="absolute inset-0" onClick={onClose} />

        {hasPrev && !isTouch && (
          <button
            onClick={e => { e.stopPropagation(); onPrev(); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full flex items-center justify-center text-white transition active:scale-90"
            style={{ background: 'rgba(255,255,255,0.15)', border: '1.5px solid rgba(255,255,255,0.25)', backdropFilter: 'blur(8px)' }}
          >
            <Icon name="ChevronRight" size={26} />
          </button>
        )}
        {hasNext && !isTouch && (
          <button
            onClick={e => { e.stopPropagation(); onNext(); }}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full flex items-center justify-center text-white transition active:scale-90"
            style={{ background: 'rgba(255,255,255,0.15)', border: '1.5px solid rgba(255,255,255,0.25)', backdropFilter: 'blur(8px)' }}
          >
            <Icon name="ChevronLeft" size={26} />
          </button>
        )}

        <div
          className="relative z-[1] flex items-center justify-center w-full h-full"
          style={{ padding: isTouch ? '0 0.5rem' : '0 3.5rem' }}
        >
          {isVideo ? (
            fullUrl ? (
              <video
                src={fullUrl} controls autoPlay
                className="max-w-full max-h-full rounded-xl shadow-2xl"
                onClick={e => e.stopPropagation()}
                onContextMenu={e => e.preventDefault()}
              />
            ) : (
              <div className="flex flex-col items-center gap-4 p-8">
                {thumbUrl && (
                  <img src={thumbUrl} alt="" draggable={false}
                    className="max-w-full rounded-xl object-contain opacity-40"
                    style={{ maxHeight: '50vh', filter: 'blur(6px)' }}
                  />
                )}
                <div className="flex items-center gap-3 text-white bg-black/60 px-5 py-3 rounded-full backdrop-blur-sm">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin shrink-0" />
                  <span className="text-sm font-medium">جاري تحميل الفيديو…</span>
                </div>
              </div>
            )
          ) : (
            <div className="relative flex items-center justify-center w-full h-full" onClick={e => e.stopPropagation()}>
              {thumbUrl && (
                <img
                  src={thumbUrl} alt="" draggable={false}
                  className={`max-w-full max-h-full rounded-xl object-contain absolute inset-0 m-auto transition-opacity duration-500
                    ${imgReady ? 'opacity-0' : 'opacity-100'}
                    ${fullUrl && !imgReady ? 'blur-sm' : ''}`}
                  onContextMenu={e => e.preventDefault()}
                  style={{ WebkitTouchCallout: 'none' }}
                />
              )}
              {fullUrl && (
                <img
                  key={fullUrl}
                  src={fullUrl} alt="" draggable={false}
                  className={`max-w-full max-h-full rounded-xl shadow-2xl object-contain relative transition-opacity duration-500 ${imgReady ? 'opacity-100' : 'opacity-0'}`}
                  onLoad={() => setImgReady(true)}
                  onContextMenu={e => e.preventDefault()}
                  style={{ WebkitTouchCallout: 'none' }}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Thumbnail strip */}
      {files.length > 1 && (
        <div className="shrink-0 pt-3 pb-4 px-4"
          style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%)' }}>
          <div className="flex gap-2 overflow-x-auto justify-center" style={{ scrollbarWidth: 'none' }}>
            {files.map((f, i) => (
              <button
                key={f.id}
                onClick={() => onJump(i)}
                className={`shrink-0 w-14 h-14 rounded-lg overflow-hidden transition-all ${
                  i === currentIndex
                    ? 'ring-2 ring-white ring-offset-1 ring-offset-black scale-105 opacity-100'
                    : 'opacity-50 hover:opacity-80'
                }`}
              >
                {thumbUrls[f.id] ? (
                  <img src={thumbUrls[f.id]} alt="" className="w-full h-full object-cover" draggable={false} />
                ) : (
                  <div className="w-full h-full bg-white/10 flex items-center justify-center">
                    <Icon name={f.file_type === 'video' ? 'Film' : 'Image'} size={16} className="text-white/60" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const AlbumViewerModal = ({
  files, urlMap, delivery,
  lightboxIndex, fullUrls,
  onOpenLightbox, onCloseLightbox, onNavigate,
  onClose, onDownload, onNativeSave,
  downloading, nativeSaving, nativeSaveProgress, isNative,
}) => {
  // Lock body scroll + hide bottom nav while modal is open
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('album-viewer-open');
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.classList.remove('album-viewer-open');
    };
  }, []);

  // Build thumbUrl map: file.id → URL
  const thumbUrls = {};
  files.forEach(f => {
    const key = f.thumbnail_path || f.file_path;
    if (urlMap[key]) thumbUrls[f.id] = urlMap[key];
  });

  const formatSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024, s = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + s[i];
  };

  const photos = files.filter(f => f.file_type === 'image').length;
  const videos = files.filter(f => f.file_type === 'video').length;

  return (
    <div className="fixed inset-0 z-[150] bg-[#0a0a0a] flex flex-col" dir="rtl">

      {/* ── Header ── */}
      <div className="shrink-0 flex items-center gap-3 px-4 border-b border-white/8"
        style={{
          background: 'rgba(10,10,10,0.98)',
          backdropFilter: 'blur(20px)',
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)',
          paddingBottom: '12px',
        }}>
        <button
          onClick={onClose}
          className="w-9 h-9 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition"
        >
          <Icon name="X" size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-semibold truncate">
            {getPhotographyTypeLabelAr(delivery?.booking?.photography_type) || 'ألبوم الجلسة'}
          </p>
          <p className="text-white/40 text-xs">
            {photos > 0 && `${photos} صورة`}
            {photos > 0 && videos > 0 && ' · '}
            {videos > 0 && `${videos} فيديو`}
            {delivery?.total_size_bytes > 0 && ` · ${formatSize(delivery.total_size_bytes)}`}
          </p>
        </div>
      </div>

      {/* ── Gallery grid ── */}
      <div className="flex-1 overflow-y-auto px-2 py-3" style={{ scrollbarWidth: 'thin', scrollbarColor: '#333 transparent' }}>
        {files.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-white/30">
            <Icon name="ImageOff" size={48} />
            <p className="text-sm">لا توجد ملفات</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-1.5">
            {files.map((file, idx) => {
              const thumb = thumbUrls[file.id];
              return (
                <div
                  key={file.id}
                  className="aspect-square rounded-lg overflow-hidden bg-white/5 cursor-pointer relative group select-none active:scale-[0.96] transition-transform"
                  onClick={() => onOpenLightbox(idx)}
                  onContextMenu={e => e.preventDefault()}
                  style={{ WebkitTouchCallout: 'none' }}
                >
                  {thumb ? (
                    <img
                      src={thumb} alt="" loading="lazy" draggable={false}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      onContextMenu={e => e.preventDefault()}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon name={file.file_type === 'video' ? 'Film' : 'Image'} size={24} className="text-white/20" />
                    </div>
                  )}
                  {file.file_type === 'video' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-black/60 border border-white/20 flex items-center justify-center backdrop-blur-sm">
                        <div className="w-0 h-0 border-l-[10px] border-l-white border-y-[6px] border-y-transparent ml-0.5" />
                      </div>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Action bar ── */}
      <div className="shrink-0 px-4 py-4 space-y-2.5 border-t border-white/8"
        style={{ background: 'rgba(10,10,10,0.98)', backdropFilter: 'blur(20px)' }}>
        {/* تحميل الألبوم — ويب فقط */}
        {!isNative && (
          <button
            onClick={onDownload}
            disabled={downloading || nativeSaving}
            className="w-full flex items-center justify-center gap-2 bg-white hover:bg-white/90 disabled:opacity-50 text-slate-900 font-bold py-3.5 rounded-2xl transition text-sm"
          >
            {downloading
              ? <><span className="w-4 h-4 border-2 border-slate-400 border-t-slate-800 rounded-full animate-spin" />جاري التحضير...</>
              : <><Icon name="DownloadCloud" size={18} />تحميل الألبوم كاملاً</>}
          </button>
        )}

        {/* حفظ في استوديو الصور — iOS فقط */}
        {isNative && (
          <div>
            <button
              onClick={onNativeSave}
              disabled={nativeSaving || downloading}
              className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold py-3.5 rounded-2xl transition text-sm"
            >
              {nativeSaving && nativeSaveProgress
                ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    جاري الحفظ {nativeSaveProgress.current} من {nativeSaveProgress.total}...</>
                : <><Icon name="GalleryHorizontalEnd" size={18} />حفظ في استوديو الصور</>}
            </button>

            {/* شريط تقدم الحفظ */}
            {nativeSaving && nativeSaveProgress && nativeSaveProgress.total > 0 && (
              <div className="mt-2 px-1">
                <div className="flex justify-between text-xs text-white/50 mb-1">
                  <span>
                    {nativeSaveProgress.current} / {nativeSaveProgress.total}
                    {nativeSaveProgress.failed > 0 && <span className="text-red-400 mr-1">({nativeSaveProgress.failed} فشل)</span>}
                  </span>
                  <span>{Math.round((nativeSaveProgress.current / nativeSaveProgress.total) * 100)}%</span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-1.5">
                  <div
                    className="bg-emerald-500 h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${(nativeSaveProgress.current / nativeSaveProgress.total) * 100}%` }}
                  />
                </div>
                <p className="text-xs text-amber-400/80 mt-2 text-center">
                  لا تغادر هذه الصفحة حتى اكتمال الحفظ
                </p>
              </div>
            )}

            {/* رسالة اكتمال الحفظ */}
            {!nativeSaving && nativeSaveProgress?.phase === 'done' && (
              <div className={`mt-2 rounded-xl px-3 py-2 text-xs text-center font-medium ${
                nativeSaveProgress.failed === 0
                  ? 'bg-emerald-900/50 text-emerald-400 border border-emerald-700/40'
                  : 'bg-amber-900/50 text-amber-400 border border-amber-700/40'
              }`}>
                {nativeSaveProgress.failed === 0
                  ? `✅ تم الحفظ في ألبوم "مشهد"`
                  : `⚠️ تم حفظ ${nativeSaveProgress.total - nativeSaveProgress.failed} من ${nativeSaveProgress.total} ملف`}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Lightbox ── */}
      {lightboxIndex !== null && (
        <AlbumLightbox
          files={files}
          currentIndex={lightboxIndex}
          thumbUrls={thumbUrls}
          fullUrls={fullUrls}
          onClose={onCloseLightbox}
          onPrev={() => onNavigate(Math.max(0, lightboxIndex - 1))}
          onNext={() => onNavigate(Math.min(files.length - 1, lightboxIndex + 1))}
          onJump={onNavigate}
        />
      )}
    </div>
  );
};

export default MyDeliveries;
